import type { NotifyEvent, PushTriggerDto, UpdatePushTriggersDto } from '@schwrz/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '../shared/api/client';
import {
  fetchPushStatus,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../shared/lib/push';

export const usePushStore = defineStore('push', () => {
  const triggers = ref<PushTriggerDto[]>([]);
  const loading = ref(false);
  const supported = ref(isPushSupported());
  const subscribed = ref(false);
  const statusLoaded = ref(false);

  function triggerOf(event: NotifyEvent): boolean {
    return triggers.value.find((t) => t.event === event)?.enabled ?? false;
  }

  async function loadTriggers(): Promise<void> {
    loading.value = true;
    try {
      const res = await api<{ triggers: PushTriggerDto[] }>('/api/push/triggers');
      triggers.value = res.triggers;
    } finally {
      loading.value = false;
    }
  }

  async function setTrigger(event: NotifyEvent, enabled: boolean): Promise<void> {
    const idx = triggers.value.findIndex((t) => t.event === event);
    const existing = idx >= 0 ? triggers.value[idx] : undefined;
    const previous = existing?.enabled ?? false;
    if (existing) {
      triggers.value[idx] = { ...existing, enabled };
    } else {
      triggers.value = [...triggers.value, { event, enabled, updatedAt: new Date().toISOString() }];
    }
    try {
      const payload: UpdatePushTriggersDto = { triggers: [{ event, enabled }] };
      await api<{ ok: true; count: number }>('/api/push/triggers', { method: 'PUT', body: payload });
    } catch (err) {
      if (existing) {
        triggers.value[idx] = { ...existing, enabled: previous };
      } else {
        triggers.value = triggers.value.filter((t) => t.event !== event);
      }
      throw err;
    }
  }

  async function refreshStatus(): Promise<void> {
    supported.value = isPushSupported();
    if (!supported.value) {
      subscribed.value = false;
      statusLoaded.value = true;
      return;
    }
    try {
      const status = await fetchPushStatus();
      subscribed.value = status.subscribed;
    } catch {
      subscribed.value = false;
    } finally {
      statusLoaded.value = true;
    }
  }

  async function enable(): Promise<string | null> {
    const result = await subscribeToPush();
    if (!result.ok) return result.reason;
    subscribed.value = true;
    return null;
  }

  async function disable(): Promise<void> {
    await unsubscribeFromPush();
    subscribed.value = false;
  }

  return {
    triggers,
    loading,
    supported,
    subscribed,
    statusLoaded,
    triggerOf,
    loadTriggers,
    setTrigger,
    refreshStatus,
    enable,
    disable,
  };
});
